import { useEffect, useState } from "react";
import { Formik, Form } from "formik";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import { ITaxCategory } from "./TaxCategoryController";
import {
  fetchTaxCategoryById,
  handleSubmitTaxCategory,
  ITaxCategoryForm,
  TAX_CATEGORY_INITIAL_VALUES,
  taxCategoryValidationSchema,
} from "./TaxCategoryFormController";
import { hasAnyModification } from "../../utils/AppFunctions";
import {
  FormRightModalWrapper,
  ICButton,
  ICConfirmationModal,
  Loader,
} from "../../core-component/ui";
import { ICFormikTextInput } from "../../core-component/formik";
import { ICCheckbox } from "../../core-component/form";
import { BIT_VALUE } from "../../utils/AppEnumerations";

interface ITaxCategoryFormProps {
  handleClose: (status: boolean) => void;
  readOnly: boolean;
  taxCategoryToEdit?: ITaxCategory;
}

const TaxCategoryForm: React.FC<ITaxCategoryFormProps> = ({
  handleClose,
  readOnly,
  taxCategoryToEdit,
}) => {
  const { t } = UseTranslationHook();
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [showDiscardWarning, setShowDiscardWarning] = useState(false);
  const [taxCategoryFormInitialValues, setTaxCategoryFormInitialValues] =
    useState<ITaxCategoryForm>();

  useEffect(() => {
    if (taxCategoryToEdit) {
      getTaxCategoryFieldById(taxCategoryToEdit.id);
    } else {
      setTaxCategoryFormInitialValues(TAX_CATEGORY_INITIAL_VALUES);
    }
  }, []);

  const getTaxCategoryFieldById = async (id: string) => {
    setLoading(true);
    await fetchTaxCategoryById(
      id,
      handleClose,
      setTaxCategoryFormInitialValues
    );
    setLoading(false);
  };

  const checkFormModification = (values: ITaxCategoryForm) => {
    if (
      taxCategoryFormInitialValues &&
      hasAnyModification(values, taxCategoryFormInitialValues)
    ) {
      setShowDiscardWarning(true);
    } else {
      handleClose(false);
    }
  };
  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <>
          {taxCategoryFormInitialValues && (
            <Formik
              initialValues={taxCategoryFormInitialValues}
              validationSchema={taxCategoryValidationSchema(t)}
              onSubmit={(values) => {
                handleSubmitTaxCategory(
                  values,
                  setSubmitLoading,
                  taxCategoryToEdit?.id,
                  handleClose
                );
              }}
            >
              {(formikPorps) => {
                const { handleSubmit, values } = formikPorps;

                return (
                  <FormRightModalWrapper
                    containerClassName="!w-1/4"
                    onClose={() => checkFormModification(values)}
                    title={t("tax_category")}
                  >
                    <Form
                      onSubmit={handleSubmit}
                      className="flex flex-col h-full"
                    >
                      <div className="flex-1 space-y-4">
                        <ICFormikTextInput
                          {...formikPorps}
                          fieldName={"category_name"}
                          placeholder={t("category_name")}
                          required
                          tabIndex={0}
                          disabled={readOnly}
                        />
                        <ICCheckbox
                          {...formikPorps}
                          label={t("can_individual_assign_to_item")}
                          checked={
                            formikPorps?.values
                              ?.can_individual_assign_to_item === BIT_VALUE.One
                          }
                          onChange={(option) => {
                            formikPorps?.setFieldValue(
                              "can_individual_assign_to_item",
                              option ? BIT_VALUE.One : BIT_VALUE.Zero
                            );
                          }}
                          disabled={readOnly}
                        />
                      </div>
                      {!readOnly && (
                        <ICButton
                          loading={submitLoading}
                          className={"w-full"}
                          children={t("submit")}
                        />
                      )}
                    </Form>
                  </FormRightModalWrapper>
                );
              }}
            </Formik>
          )}
          {showDiscardWarning && (
            <ICConfirmationModal
              title={t("discard_warning")}
              message={t("discard_unsaved_changes")}
              onClose={(value) => {
                setShowDiscardWarning(false);
                value && handleClose(false);
              }}
            />
          )}
        </>
      )}
    </>
  );
};

export default TaxCategoryForm;
